import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaCheckCircle, FaLinkedin, FaTwitter, FaYoutube, FaExclamationCircle, FaCheckCircle as FaCheck } from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';
import './InstructorCriteria.css';
import api from '../../api';
import { useAuth } from '../../context/AuthContext'; // ✅ Import Auth Context
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import '../InstructorAbout/InstructorAbout.css';

const validateUrl = (url, type) => {
  if (!url) return true; // Empty URLs are valid
  const patterns = {
    linkedin: /^https?:\/\/(www\.)?linkedin\.com\/in\/[\w-]+\/?$/,
    twitter: /^https?:\/\/(www\.)?twitter\.com\/[\w-]+\/?$/,
    youtube: /^https?:\/\/(www\.)?youtube\.com\/(c\/|channel\/)?[\w-]+\/?$/,
  };
  return patterns[type].test(url);
};

const InstructorCriteria = () => {
  useBlockCheck('instructor'); // 🔥 Prevents blocked users from accessing this page
  const navigate = useNavigate();
  const { profile, token } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(false);// ✅ Prevent multiple submissions
  const [showBioForm, setShowBioForm] = useState(false); // State to show/hide bio form
  const [wordCount, setWordCount] = useState(0); // Add wordCount state
  const [bioData, setBioData] = useState({
    bio: '',
    aboutMe: '',
    linkedin: '',
    twitter: '',
    youtube: '',
  });

  // Add handleKeyPress function
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && selectedOptions[step]) {
      nextStep();
    }
  };

  useEffect(() => {
    // Fetch criteria from the backend API
    axios
      .get(api.getInstructorCriteria)
      .then((response) => setCriteria(response.data))
      .catch((error) => {
        console.error('Error fetching criteria:', error);
        Swal.fire({
          icon: 'error',
          title: 'Failed to Load Criteria',
          text: 'There was an error fetching the criteria.',
          confirmButtonColor: '#dc3545',
        });
      });
  }, []);

  useEffect(() => {
    // Add event listener for keyboard events
    document.addEventListener('keypress', handleKeyPress);

    // Cleanup the event listener
    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [step, selectedOptions]); // Dependencies for the effect

  // ✅ Handle option selection
  const handleOptionSelect = (stepNumber, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [stepNumber]: value, // Store selected value for the current step
    }));
  };

  // ✅ Move to next step
  const nextStep = () => {
    if (selectedOptions[step]) {
      if (step < criteria.length) {
        setStep((prev) => prev + 1);
      } else {
        setShowBioForm(true); // Show bio form after the last step
      }
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select an option before proceeding.',
        confirmButtonColor: '#007bff',
      });
    }
  };

  // ✅ Move to previous step
  const prevStep = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    }
  };

  const handleBioChanges = (e) => {
    const { name, value } = e.target;
    setBioData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleBioChange = (e) => {
    const { name, value } = e.target;

    // Split value into words (filter out empty strings)
    const wordsArray = value.trim().split(/\s+/).filter(Boolean);
    const currentWordCount = wordsArray.length;

    // Update word count
    setWordCount(currentWordCount);

    // Limit to 10 words max
    if (currentWordCount <= 10) {
      setBioData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      // If user pastes/enters more than 10 words, only keep the first 10
      const limitedValue = wordsArray.slice(0, 10).join(' ');
      setBioData((prev) => ({
        ...prev,
        [name]: limitedValue,
      }));
    }
  };


  // Validation for bio form: both bio and aboutMe must be non-empty
  const isBioFormValid = () => {
    return bioData.bio.trim() !== '' && bioData.aboutMe.trim() !== '';
  };

  const handleSubmit = async () => {
    if (!profile || !profile._id) {
      Swal.fire({
        icon: 'error',
        title: 'User Not Found',
        text: 'Please log in before submitting your application.',
        confirmButtonColor: '#dc3545',
      });
      return;
    }

    if (!isBioFormValid()) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Bio',
        text: 'Please fill in both Short Bio and About Me before submitting.',
        confirmButtonColor: '#007bff',
      });
      return;
    }

    try {
      setLoading(true); // Prevent multiple clicks

      // ✅ Convert selected options into { criteriaId, selectedOptionId }
      const formattedSubmission = Object.entries(selectedOptions).map(
        ([stepIndex, selectedOptionLabel]) => {
          const criteriaItem = criteria[stepIndex - 1]; // Get criteria object
          const selectedOption = criteriaItem.options.find(
            (opt) => opt.label === selectedOptionLabel,
          );
          return {
            criteriaId: criteriaItem._id, // Store criteria ID
            selectedOptionId: selectedOption._id, // Store selected option ID
          };
        },
      );

      // Submit criteria and bio data
      const response = await axios.post(api.submitInstructorCriteria, {
        userId: profile._id,
        selectedCriteria: formattedSubmission,
        aboutInstructor: {
          bio: bioData.bio,
          aboutMe: bioData.aboutMe,
          socialLinks: {
            linkedin: bioData.linkedin,
            twitter: bioData.twitter,
            youtube: bioData.youtube,
          },
        },
      });

      Swal.fire({
        icon: 'success',
        title: 'Application Submitted!',
        text: response.data.message || 'Your instructor application has been submitted successfully.',
        confirmButtonColor: '#28a745',
      }).then(() => {
        navigate('/dashboard');
      });
    } catch (error) {
      console.error('Error submitting criteria:', error.response?.data || error.message);
      Swal.fire({
        icon: 'error',
        title: 'Submission Failed',
        text: error.response?.data?.message || 'There was an error submitting your application. Please try again.',
        confirmButtonColor: '#dc3545',
      });
    } finally {
      setLoading(false); // ✅ Enable button after submission
    }
  };

  // Add function to go back from bio form
  const handleBackFromBio = () => {
    setShowBioForm(false);
    setStep(criteria.length);
  };

  return (
    <div>
      <div className="icp-criteria-container">
        {/* Header Section */}
        <div className="icp-header-section">
          <button className="icp-exit-btn" onClick={() => navigate('/home')}>
            Exit
          </button>
          <h1 className="icp-be">Become an Instructor</h1>
          <p className="icp-sh">
            Share your knowledge, inspire learners, and earn money by teaching
            on our platform. Start your journey today!
          </p>
        </div>

        {/* Progress Bar */}
        <div className="icp-progress-container">
          <div
            className="icp-progress-bar"
            style={{ width: `${((showBioForm ? criteria.length + 1 : step) / (criteria.length + 1)) * 100}%` }}
          ></div>
        </div>
        <p className="icp-step-count">
          Step {showBioForm ? criteria.length + 1 : step} of {criteria.length + 1}
        </p>

        {/* Step Content */}
        {!showBioForm ? (
          <div className="icp-step-card">
            {criteria.length > 0 &&
              criteria.map(
                (stepData, index) =>
                  step === index + 1 && (
                    <div key={index} className="icp-step-content">
                      <h3>{stepData.title}</h3>
                      {stepData.options.map((option, i) => (
                        <div
                          key={i}
                          className={`icp-option-box ${selectedOptions[step] === option.label
                            ? 'icp-selected'
                            : ''
                          }`}
                          onClick={() => handleOptionSelect(step, option.label)}
                          tabIndex={0}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleOptionSelect(step, option.label);
                            }
                          }}
                        >
                          <input
                            type="radio"
                            name={`step${step}`}
                            value={option.label}
                            checked={selectedOptions[step] === option.label}
                            onChange={() =>
                              handleOptionSelect(step, option.label)
                            }
                          />
                          {option.label}
                        </div>
                      ))}
                    </div>
                  ),
              )}
          </div>
        ) : (
          <div className="icp-about-section">
            <h3>Tell Us About Yourself</h3>
            <div>
              <h4>Short Bio</h4>
              <textarea
                id="bio"
                name="bio"
                value={bioData.bio}
                onChange={handleBioChange}
                placeholder="Write a brief professional bio (10 words max)..."
                required
                className={`icp-bio-textarea ${bioData.bio.trim() ? 'icp-form-field-valid' : ''}`}
                style={{ height: '40px' }}
              />
              <div className={`icp-bio-word-counts ${wordCount >= 8 ? 'icp-near-limit' : ''
              } ${wordCount >= 10 ? 'icp-at-limit' : ''
              }`}>
                <span>{wordCount} / 10</span>
                {wordCount > 0 && (
                  <span className="icp-validation-message icp-success">
                    <FaCheck /> Bio added
                  </span>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="aboutMe">About Me</label>
              <textarea
                id="aboutMe"
                name="aboutMe"
                value={bioData.aboutMe}
                onChange={handleBioChanges}
                placeholder="Tell us more about your professional background, expertise, and teaching experience..."
                rows="6"
                required
                className={`icp-about-section-row ${bioData.aboutMe.trim() ? 'icp-form-field-valid' : ''}`}
              />
              {bioData.aboutMe.trim() && (
                <span className="icp-validation-message icp-success">
                  <FaCheck /> About me section completed
                </span>
              )}
            </div>

            <div className="icp-about-social-form">
              <h4>Social Media Profiles</h4>
              <div className="icp-social-input-group">
                <div className="icp-input-wrapper">
                  <FaLinkedin className="icp-social-input-icon" />
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={bioData.linkedin}
                    onChange={handleBioChanges}
                    placeholder="https://linkedin.com/in/your-profile"
                    className={bioData.linkedin ? (validateUrl(bioData.linkedin, 'linkedin') ? 'icp-form-field-valid' : 'icp-form-field-invalid') : ''}
                  />
                </div>
                {bioData.linkedin && !validateUrl(bioData.linkedin, 'linkedin') && (
                  <span className="icp-validation-message icp-error">
                    <FaExclamationCircle /> Please enter a valid LinkedIn profile URL
                  </span>
                )}
              </div>

              <div className="icp-social-input-group">
                <div className="icp-input-wrapper">
                  <FaTwitter className="icp-social-input-icon" />
                  <input
                    type="url"
                    id="twitter"
                    name="twitter"
                    value={bioData.twitter}
                    onChange={handleBioChanges}
                    placeholder="https://twitter.com/your-profile"
                    className={bioData.twitter ? (validateUrl(bioData.twitter, 'twitter') ? 'icp-form-field-valid' : 'icp-form-field-invalid') : ''}
                  />
                </div>
                {bioData.twitter && !validateUrl(bioData.twitter, 'twitter') && (
                  <span className="icp-validation-message icp-error">
                    <FaExclamationCircle /> Please enter a valid Twitter profile URL
                  </span>
                )}
              </div>

              <div className="icp-social-input-group">
                <div className="icp-input-wrapper">
                  <FaYoutube className="icp-social-input-icon" />
                  <input
                    type="url"
                    id="youtube"
                    name="youtube"
                    value={bioData.youtube}
                    onChange={handleBioChanges}
                    placeholder="https://youtube.com/your-channel"
                    className={bioData.youtube ? (validateUrl(bioData.youtube, 'youtube') ? 'icp-form-field-valid' : 'icp-form-field-invalid') : ''}
                  />
                </div>
                {bioData.youtube && !validateUrl(bioData.youtube, 'youtube') && (
                  <span className="icp-validation-message icp-error">
                    <FaExclamationCircle /> Please enter a valid YouTube channel URL
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="icp-button-container">
          {/* Show back button if not in first step OR if in bio form */}
          {(step > 1 || showBioForm) && (
            <button
              onClick={showBioForm ? handleBackFromBio : prevStep}
              className="icp-prev-btn"
            >
              <FaArrowLeft /> Back
            </button>
          )}
          {!showBioForm ? (
            <button onClick={nextStep} className="icp-next-btn">
              Next <FaArrowRight />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="icp-submit-btn"
              disabled={loading || !isBioFormValid()}
            >
              {loading ? 'Submitting...' : 'Submit'} <FaCheckCircle />
            </button>
          )}
        </div>

        {/* Footer Section */}
        <div className="icp-footer-section">
          <h3>Why Become an Instructor?</h3>
          <ul>
            <li>Reach millions of learners worldwide.</li>
            <li>Earn money by sharing your expertise.</li>
            <li>Join a supportive community of instructors.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InstructorCriteria;
